import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Phone,
  Users,
  MessageSquare,
  Mail,
  Coffee,
  UtensilsCrossed,
  Video,
  MoreHorizontal,
  Loader2,
  ChevronDown,
  ChevronUp,
  Search,
  UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PlaceAutocomplete } from '@/components/PlaceAutocomplete'
import { useUpdateInteraction } from '@/hooks/use-interactions'
import { useContacts } from '@/hooks/use-contacts'
import type { Interaction, InteractionType, InteractionQuality, Contact } from '@/lib/types'

const INTERACTION_TYPES: { type: InteractionType; icon: typeof Phone; label: string }[] = [
  { type: 'call', icon: Phone, label: 'Call' },
  { type: 'meeting', icon: Users, label: 'Meeting' },
  { type: 'text', icon: MessageSquare, label: 'Text' },
  { type: 'email', icon: Mail, label: 'Email' },
  { type: 'coffee', icon: Coffee, label: 'Coffee' },
  { type: 'dinner', icon: UtensilsCrossed, label: 'Dinner' },
  { type: 'video_call', icon: Video, label: 'Video' },
  { type: 'other', icon: MoreHorizontal, label: 'Other' },
]

const QUALITY_OPTIONS: {
  value: InteractionQuality
  emoji: string
  label: string
  color: string
}[] = [
  {
    value: 'great',
    emoji: '\u{1F929}',
    label: 'Great',
    color: 'bg-chartreuse-soft text-chartreuse-foreground ring-chartreuse',
  },
  {
    value: 'good',
    emoji: '\u{1F60A}',
    label: 'Good',
    color: 'bg-lilac-soft text-lilac-foreground ring-lilac',
  },
  {
    value: 'neutral',
    emoji: '\u{1F610}',
    label: 'Neutral',
    color: 'bg-muted text-muted-foreground ring-outline',
  },
  {
    value: 'poor',
    emoji: '\u{1F61E}',
    label: 'Poor',
    color: 'bg-coral-soft text-coral ring-coral',
  },
]

function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

interface EditInteractionModalProps {
  interaction: Interaction
  onClose: () => void
}

function getInitials(firstName: string, lastName: string | null): string {
  return firstName.charAt(0).toUpperCase() + (lastName?.charAt(0).toUpperCase() ?? '')
}

function getContactName(c: { firstName: string; lastName: string | null }): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ')
}

export function EditInteractionModal({ interaction, onClose }: EditInteractionModalProps) {
  const { t } = useTranslation()
  const updateInteraction = useUpdateInteraction()

  const [type, setType] = useState<InteractionType>(interaction.type as InteractionType)
  const [occurredAt, setOccurredAt] = useState(
    formatDateTimeLocal(new Date(interaction.occurredAt)),
  )
  const [title, setTitle] = useState(interaction.title || '')
  const [notes, setNotes] = useState(interaction.notes || '')
  const [formLocation, setFormLocation] = useState(interaction.location || '')
  const [meetingContext, setMeetingContext] = useState(interaction.meetingContext || '')
  const [showMeetingContext, setShowMeetingContext] = useState(!!interaction.meetingContext)
  const [quality, setQuality] = useState<InteractionQuality | null>(
    (interaction.quality as InteractionQuality) || null,
  )

  // Participants state (excludes primary contact)
  const [selectedContacts, setSelectedContacts] = useState<
    { id: string; firstName: string; lastName: string | null; avatarUrl: string | null }[]
  >(() => {
    if (interaction.participants?.length) {
      return interaction.participants.filter((p) => p.id !== interaction.contactId)
    }
    return []
  })
  const [contactSearch, setContactSearch] = useState('')
  const [showContactPicker, setShowContactPicker] = useState(false)
  const { data: contactResults } = useContacts({ q: contactSearch, limit: 10 })

  const filteredResults = contactResults?.items.filter(
    (c) => c.id !== interaction.contactId && !selectedContacts.some((sc) => sc.id === c.id),
  )

  function addContact(c: Contact) {
    if (selectedContacts.some((sc) => sc.id === c.id)) return
    setSelectedContacts((prev) => [
      ...prev,
      { id: c.id, firstName: c.firstName, lastName: c.lastName, avatarUrl: c.avatarUrl },
    ])
    setContactSearch('')
    setShowContactPicker(false)
  }

  function removeContact(contactId: string) {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId))
  }

  function handleSave() {
    const contactIds = [interaction.contactId, ...selectedContacts.map((c) => c.id)]

    updateInteraction.mutate(
      {
        id: interaction.id,
        contact_ids: contactIds,
        type,
        title: title.trim() || null,
        notes: notes.trim() || null,
        location: formLocation.trim() || null,
        meeting_context: meetingContext.trim() || null,
        quality,
        occurred_at: new Date(occurredAt).toISOString(),
      },
      { onSuccess: () => onClose() },
    )
  }

  const sheetRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  function handleClose() {
    setVisible(false)
    setTimeout(onClose, 200)
  }

  // Animate in on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true))
  }, [])

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleClose])

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-end justify-center p-0 transition-colors duration-200 sm:items-center sm:p-4',
        visible ? 'bg-black/50' : 'bg-black/0',
      )}
      onClick={handleClose}
    >
      <div
        ref={sheetRef}
        className={cn(
          'bg-surface-container-low flex max-h-[85vh] w-full max-w-md flex-col rounded-t-2xl shadow-lg transition-transform duration-200 ease-out sm:rounded-2xl',
          visible ? 'translate-y-0' : 'translate-y-full sm:translate-y-8',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex shrink-0 justify-center pt-3 pb-1 sm:hidden">
          <div className="bg-outline-variant/40 h-1 w-8 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-5 pt-2 pb-3 sm:pt-4">
          <h2 className="font-heading text-base font-semibold">
            {t('interactions.edit', 'Edit Interaction')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 px-5 pb-5">
            {/* Type selector */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('interactions.type', 'Type')}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {INTERACTION_TYPES.map(({ type: t, icon: Icon, label }) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors',
                      type === t
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Other participants */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('interactions.otherParticipants', 'Other participants')}
              </label>

              {/* Selected contacts chips */}
              {selectedContacts.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedContacts.map((c) => (
                    <div
                      key={c.id}
                      className="bg-surface-container flex items-center gap-1.5 rounded-full py-1 pr-2 pl-1"
                    >
                      {c.avatarUrl ? (
                        <img
                          src={c.avatarUrl}
                          alt=""
                          className="size-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="bg-primary/10 text-primary flex size-6 items-center justify-center rounded-full text-[10px] font-semibold">
                          {getInitials(c.firstName, c.lastName)}
                        </div>
                      )}
                      <span className="text-xs font-medium">{getContactName(c)}</span>
                      <button
                        type="button"
                        onClick={() => removeContact(c.id)}
                        className="text-muted-foreground hover:text-foreground ml-0.5"
                      >
                        <X className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add participant button / search */}
              {showContactPicker ? (
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder={t('interactions.searchContact', 'Search contacts...')}
                    className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border py-2 pr-9 pl-9 text-sm outline-none focus:ring-2"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowContactPicker(false)
                      setContactSearch('')
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X className="size-4" />
                  </button>
                  {contactSearch && filteredResults && filteredResults.length > 0 && (
                    <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 left-0 z-10 mt-1 max-h-48 overflow-y-auto rounded-xl shadow-lg backdrop-blur-xl">
                      {filteredResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="hover:bg-primary/5 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                          onClick={() => addContact(c)}
                        >
                          {c.avatarUrl ? (
                            <img
                              src={c.avatarUrl}
                              alt=""
                              className="size-7 rounded-full object-cover"
                            />
                          ) : (
                            <div className="bg-primary/10 text-primary flex size-7 items-center justify-center rounded-full text-xs font-semibold">
                              {getInitials(c.firstName, c.lastName)}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">{getContactName(c)}</span>
                            {c.company && (
                              <span className="text-muted-foreground ml-1 text-xs">
                                {c.company}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowContactPicker(true)}
                  className="border-outline-variant/30 hover:bg-surface-container-high text-on-surface-variant flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition-colors"
                >
                  <UserPlus className="size-4" />
                  {t('interactions.addParticipant', 'Add participant')}
                </button>
              )}
            </div>

            {/* Date/time */}
            <div>
              <label htmlFor="edit-occurred-at" className="mb-2 block text-sm font-medium">
                {t('interactions.when', 'When')}
              </label>
              <input
                id="edit-occurred-at"
                type="datetime-local"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              />
            </div>

            {/* Title */}
            <div>
              <label htmlFor="edit-title" className="mb-2 block text-sm font-medium">
                {t('interactions.title', 'Title')}
              </label>
              <input
                id="edit-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('interactions.titlePlaceholder', 'Optional title...')}
                className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="edit-notes" className="mb-2 block text-sm font-medium">
                {t('interactions.notes', 'Notes')}
              </label>
              <textarea
                id="edit-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('interactions.notesPlaceholder', 'What did you talk about?')}
                className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              />
            </div>

            {/* Location */}
            <div>
              <label htmlFor="edit-location" className="mb-2 block text-sm font-medium">
                {t('interactions.location', 'Location')}
              </label>
              <PlaceAutocomplete
                id="edit-location"
                value={formLocation}
                onChange={setFormLocation}
                placeholder={t('interactions.locationPlaceholder', 'Optional location...')}
                className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              />
            </div>

            {/* Meeting context (expandable) */}
            <div>
              <button
                type="button"
                onClick={() => setShowMeetingContext(!showMeetingContext)}
                className="text-on-surface-variant hover:text-foreground flex w-full items-center gap-2 text-sm font-medium transition-colors"
              >
                {showMeetingContext ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
                {t('interactions.meetingContext', 'Meeting context')}
              </button>
              {showMeetingContext && (
                <textarea
                  id="edit-meeting-context"
                  rows={3}
                  value={meetingContext}
                  onChange={(e) => setMeetingContext(e.target.value)}
                  placeholder={t(
                    'interactions.meetingContextPlaceholder',
                    'When, where, how, what...',
                  )}
                  className="border-outline-variant/30 bg-surface-container focus:ring-ring mt-2 w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
                />
              )}
            </div>

            {/* Quality */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('interactions.quality', 'Quality')}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {QUALITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setQuality(quality === opt.value ? null : opt.value)}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors',
                      quality === opt.value
                        ? `${opt.color} ring-2`
                        : 'border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high',
                    )}
                  >
                    <span className="text-lg">{opt.emoji}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky save button */}
        <div className="border-outline-variant/20 shrink-0 border-t px-5 py-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateInteraction.isPending}
            className="bg-primary text-primary-foreground flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {updateInteraction.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  )
}
